// File: src/services/omnicine/export/podcastExportV1.ts
//
// Podcast Export v1 — OmniCine → publishable podcast render plan (+ optional offline WAV render)
//
// Goals:
// - One canonical, deterministic JSON contract for a “Podcast Episode” export.
// - Tolerant of legacy timeline shapes (clip.durSec vs clip.endSec; viewport vs flat zoom, etc.).
// - Optional in-browser offline render (OfflineAudioContext) when a media resolver is provided.
// - No dependencies.
//
// Notes:
// - This module does NOT assume how you store media. You inject a resolver.
// - If you only need JSON export now, call buildPodcastExportV1(...) and save payload.
//

import { encodeWavFromAudioBuffer } from "./WavWriter";

// ✅ registry-driven render hints (loop/gain/fades/LUFS trim etc.)
import { getPodcastRenderHintsFromResolved } from "@/services/media/registry/podcastMetaV1";
import type { ResolvedMediaV1 } from "@/services/media/resolve/resolveMedia";

/* =========================================================
 * Types (contract)
 * ======================================================= */

export type PodcastExportV1Format = "json" | "wav";

export type PodcastExportV1Source = "timeline" | "transport" | "commandPalette" | string;

export interface PodcastEpisodeMetaV1 {
  title?: string;
  description?: string;
  author?: string;
  language?: string;
  artwork?: { kind: "url" | "dataUrl" | "assetId"; value: string } | null;
}

export interface PodcastProvenanceV1 {
  sessionId?: string | null;
  projectId?: string | null;
  buildHash?: string | null;
  exportedAtEpochMs: number;
  source: PodcastExportV1Source;
  app: "synktribe-core-ui";
  contractVersion: 1;
}

export interface PodcastRenderTargetV1 {
  sampleRate: number; // e.g. 48000
  channels: 1 | 2; // mono/stereo
  loudnessTargetLUFS?: number; // optional (approx in v1)
  truePeakDbTP?: number; // optional clamp target (approx in v1)
}

export type PodcastTrackKindV1 = "audio" | "music" | "voice" | "sfx";

export interface PodcastTrackV1 {
  id: string;
  name?: string;
  kind: PodcastTrackKindV1;
  muted?: boolean;
  gainDb?: number; // static gain; optional
}

export interface PodcastClipV1 {
  id: string;
  trackId: string;

  startSec: number;
  endSec: number;

  // Source reference — you decide how to resolve it.
  mediaId?: string;
  url?: string;

  // Optional trim within source
  inSec?: number;
  outSec?: number;

  gainDb?: number;
  fades?: {
    inSec?: number; // fade-in duration
    outSec?: number; // fade-out duration
    curve?: "linear" | "equalPower"; // (v1 equalPower is best-effort)
  };

  meta?: Record<string, unknown>;
}

export interface PodcastMarkerV1 {
  id: string;
  timeSec: number;
  label?: string;
  kind?: string; // e.g. "chapter"
}

/**
 * ✅ v1-safe export report extension.
 * - Optional field; older v1 consumers can ignore safely.
 */
export interface PodcastExportReportV1 {
  build: {
    sampleRate: number;
    channels: 1 | 2;
    builtAtEpochMs: number;
    buildTimeMs?: number;
  };

  render?: {
    sampleRate: number;
    channels: 1 | 2;

    renderTimeMs: number;

    /** Peak dBFS from rendered buffer (NOT true-peak). */
    measuredPeakDb: number;

    /** Optional: peak before attenuation (debug/insight). */
    measuredPeakDbBefore?: number;

    /** Applied master gain (linear). */
    appliedMasterGain: number;

    /** Applied master gain (dB). */
    appliedMasterGainDb: number;
  };
}

export interface PodcastExportV1 {
  v: 1;
  episode: PodcastEpisodeMetaV1;
  provenance: PodcastProvenanceV1;
  target: PodcastRenderTargetV1;

  durationSec: number;

  tracks: PodcastTrackV1[];
  clips: PodcastClipV1[];
  markers: PodcastMarkerV1[];

  warnings: string[];

  /** ✅ v1-safe extension */
  report?: PodcastExportReportV1;
}

/* =========================================================
 * Minimal “Timeline-like” input shapes (tolerant)
 * ======================================================= */

type AnyObj = Record<string, any>;

export interface TimelineStateLike {
  durationSec?: number;

  tracks?: Array<{
    id: string;
    name?: string;
    kind?: string; // "audio" | "video" | "npc" etc.
    muted?: boolean;
    gainDb?: number;
  }>;

  clips?: Array<{
    id: string;
    trackId: string;
    startSec: number;
    endSec?: number;
    durSec?: number;

    mediaId?: string;
    url?: string;

    inSec?: number;
    outSec?: number;

    gainDb?: number;
    fades?: AnyObj;

    meta?: AnyObj;
  }>;

  markers?: Array<{
    id: string;
    timeSec: number;
    label?: string;
    kind?: string;
  }>;

  ranges?: Array<AnyObj>; // reserved for later derivations
}

/* =========================================================
 * Options
 * ======================================================= */

export interface BuildPodcastExportOptions {
  episode?: PodcastEpisodeMetaV1;

  sessionId?: string | null;
  projectId?: string | null;
  buildHash?: string | null;

  source?: PodcastExportV1Source;

  target?: Partial<PodcastRenderTargetV1>;

  includeTrackKinds?: Array<string>; // default: ["audio","music","voice","sfx"]
}

export interface RenderPodcastWavOptions {
  // Required: resolve a clip’s audio to an AudioBuffer
  resolveAudio: (ref: {
    clip: PodcastClipV1;
    track: PodcastTrackV1;
    export: PodcastExportV1;
  }) => Promise<AudioBuffer>;

  // ✅ Optional: resolve media metadata (URL/mime/meta) so we can apply registry hints
  // (loop/gain/fades/role/LUFS trim) deterministically.
  resolveMedia?: (ref: {
    clip: PodcastClipV1;
    track: PodcastTrackV1;
    export: PodcastExportV1;
  }) => Promise<ResolvedMediaV1 | null>;

  onProgress?: (p: { stage: string; done: number; total?: number }) => void;

  OfflineAudioContextCtor?: typeof OfflineAudioContext;
}

/* =========================================================
 * Helpers
 * ======================================================= */

function nowMs(): number {
  return typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function finiteNum(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

function normStr(v: unknown): string {
  return String(v ?? "").trim();
}

function safeTimeSec(v: unknown, fallback = 0): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? Math.max(0, n) : Math.max(0, fallback);
}

function trackKindToPodcastKind(k: string): PodcastTrackKindV1 {
  const s = k.toLowerCase().trim();
  if (s === "music") return "music";
  if (s === "voice") return "voice";
  if (s === "sfx") return "sfx";
  return "audio";
}

function clipEndSec(c: AnyObj): number {
  const start = safeTimeSec(c?.startSec, 0);
  if (finiteNum(c?.endSec)) return Math.max(start, Number(c.endSec));
  if (finiteNum(c?.durSec)) return Math.max(start, start + Number(c.durSec));
  return start;
}

function dbToGain(db: number): number {
  return Math.pow(10, db / 20);
}

function gainToDb(gain: number): number {
  const g = Math.max(1e-12, Number.isFinite(gain) ? gain : 0);
  return 20 * Math.log10(g);
}

function sortByTime<T extends { startSec: number; endSec: number }>(xs: T[]): T[] {
  return [...xs].sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec);
}

function sortByMarkerTime<T extends { timeSec: number }>(xs: T[]): T[] {
  return [...xs].sort((a, b) => a.timeSec - b.timeSec);
}

function inferDurationFromClips(clips: PodcastClipV1[], warnings: string[]): number {
  let maxEnd = 0;
  for (const c of clips) maxEnd = Math.max(maxEnd, c.endSec);
  if (maxEnd <= 0) warnings.push("Could not infer duration from clips; durationSec=0.");
  return maxEnd;
}

function makeId(prefix: string): string {
  try {
    const c = (globalThis as any).crypto as Crypto | undefined;
    if (c?.randomUUID) return `${prefix}-${c.randomUUID()}`;
  } catch {
    // ignore
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Best-effort equal-power curve, deterministic and cheap.
 * - For fade-in: 0..1 uses sin(t*pi/2)
 * - For fade-out: 1..0 uses cos(t*pi/2)
 */
function makeEqualPowerCurve(points: number, mode: "in" | "out", base: number): Float32Array {
  const n = Math.max(4, Math.min(256, Math.floor(points)));
  const out = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 1 : i / (n - 1);
    const v = mode === "in" ? Math.sin(t * Math.PI * 0.5) : Math.cos(t * Math.PI * 0.5);
    out[i] = Math.max(0, v * base);
  }
  return out;
}

/**
 * Wire per-clip gain + fades via scheduled GainNode.
 * - curve="linear" uses linearRampToValueAtTime
 * - curve="equalPower" uses setValueCurveAtTime (best effort)
 *
 * Hardened against 0-duration curves (OfflineAudioContext throws if duration <= 0).
 */
function wireClipGainAndFades(params: {
  ctx: OfflineAudioContext;
  source: AudioBufferSourceNode;
  clipStartSec: number;
  clipDurSec: number;
  baseGain: number; // linear
  fadeInSec: number;
  fadeOutSec: number;
  curve?: "linear" | "equalPower";
}): GainNode {
  const { ctx, source, clipStartSec, clipDurSec, baseGain, fadeInSec, fadeOutSec, curve } = params;

  const g = ctx.createGain();

  const start = Math.max(0, clipStartSec);
  const dur = Math.max(0, clipDurSec);
  const end = start + dur;

  const g0 = Math.max(0, Number.isFinite(baseGain) ? baseGain : 1);

  // Default steady gain
  g.gain.setValueAtTime(g0, start);

  // Fade-in (guard duration)
  const fin = Math.max(0, fadeInSec);
  if (fin > 0.000001) {
    const durIn = Math.max(0, fin);
    if (durIn > 0.000001) {
      if (curve === "equalPower") {
        g.gain.setValueAtTime(0, start);
        g.gain.setValueCurveAtTime(makeEqualPowerCurve(64, "in", g0), start, durIn);
        g.gain.setValueAtTime(g0, start + durIn);
      } else {
        g.gain.setValueAtTime(0, start);
        g.gain.linearRampToValueAtTime(g0, start + durIn);
      }
    } else {
      g.gain.setValueAtTime(g0, start);
    }
  }

  // Fade-out (guard duration)
  const fout = Math.max(0, fadeOutSec);
  if (fout > 0.000001) {
    const fadeStart = Math.max(start, end - fout);
    const durOut = Math.max(0, end - fadeStart);

    if (durOut > 0.000001) {
      if (curve === "equalPower") {
        g.gain.setValueAtTime(g0, fadeStart);
        g.gain.setValueCurveAtTime(makeEqualPowerCurve(64, "out", g0), fadeStart, durOut);
        g.gain.setValueAtTime(0, end);
      } else {
        g.gain.setValueAtTime(g0, fadeStart);
        g.gain.linearRampToValueAtTime(0, end);
      }
    } else {
      // Degenerate case: snap to 0 at end
      g.gain.setValueAtTime(0, end);
    }
  }

  source.connect(g);
  return g;
}

/* =========================================================
 * Patch — Decode/resample cache (module scope)
 * ======================================================= */

type AudioCacheKey = string;
const __audioCache = new Map<AudioCacheKey, Promise<AudioBuffer>>();

function makeAudioCacheKey(args: {
  clipId: string;
  mediaId?: string;
  url?: string;
  sr: number;
  ch: number;
  inSec: number;
  outSec: number | null;
}) {
  return [
    args.clipId,
    args.mediaId ?? "",
    args.url ?? "",
    `sr=${args.sr}`,
    `ch=${args.ch}`,
    `in=${args.inSec.toFixed(6)}`,
    `out=${args.outSec == null ? "" : args.outSec.toFixed(6)}`,
  ].join("|");
}

/* =========================================================
 * Patch — Peak helpers (deterministic)
 * ======================================================= */

function maxAbsSamplePeak(audio: AudioBuffer): number {
  let max = 0;

  for (let c = 0; c < audio.numberOfChannels; c++) {
    const ch = audio.getChannelData(c);
    for (let i = 0; i < ch.length; i++) {
      const v = ch[i] ?? 0;
      const a = Math.abs(Number.isFinite(v) ? v : 0);
      if (a > max) max = a;
    }
  }

  return max;
}

/**
 * True-peak-ish estimator:
 * - Always computes native sample peak
 * - Optionally checks between-sample peaks (linear interp) ONLY when gated:
 *   - sign flips OR large delta between adjacent samples
 */
function truePeakish(audio: AudioBuffer, opts?: { deltaGate?: number; steps?: number }): number {
  const deltaGate = typeof opts?.deltaGate === "number" ? Math.max(0, opts.deltaGate) : 0.12;
  const steps = typeof opts?.steps === "number" ? Math.max(2, Math.floor(opts.steps)) : 4;

  let max = 0;

  for (let c = 0; c < audio.numberOfChannels; c++) {
    const ch = audio.getChannelData(c);

    let prevRaw = ch[0] ?? 0;
    let prev = Number.isFinite(prevRaw) ? prevRaw : 0;
    max = Math.max(max, Math.abs(prev));

    for (let i = 1; i < ch.length; i++) {
      const curRaw = ch[i] ?? 0;
      const cur = Number.isFinite(curRaw) ? curRaw : 0;

      const absCur = Math.abs(cur);
      if (absCur > max) max = absCur;

      const d = cur - prev;
      const signFlip = (prev >= 0 && cur < 0) || (prev < 0 && cur >= 0);

      if (signFlip || Math.abs(d) >= deltaGate) {
        for (let s = 1; s < steps; s++) {
          const t = s / steps;
          const v = prev + (cur - prev) * t;
          const a = Math.abs(v);
          if (a > max) max = a;
        }
      }

      prev = cur;
    }
  }

  return max;
}

function applyGainInPlace(audio: AudioBuffer, gain: number) {
  const g = Number.isFinite(gain) ? gain : 1;
  if (!(g > 0) || g === 1) return;

  for (let c = 0; c < audio.numberOfChannels; c++) {
    const ch = audio.getChannelData(c);
    for (let i = 0; i < ch.length; i++) {
      const v = ch[i] ?? 0;
      const x = Number.isFinite(v) ? v : 0;
      ch[i] = x * g;
    }
  }
}

function peakDbFromLin(peakLin: number): number {
  if (!(peakLin > 0) || !Number.isFinite(peakLin)) return -Infinity;
  return 20 * Math.log10(peakLin);
}

/* =========================================================
 * 1) Build JSON contract
 * ======================================================= */

export function buildPodcastExportV1(state: TimelineStateLike, opts: BuildPodcastExportOptions = {}): PodcastExportV1 {
  const t0 = nowMs();
  const warnings: string[] = [];

  const durationSecIn = finiteNum(state?.durationSec) ? Math.max(0, Number(state.durationSec)) : 0;
  if (durationSecIn <= 0) warnings.push("durationSec is missing or <= 0 (will infer from clips).");

  const includeKinds = (opts.includeTrackKinds && opts.includeTrackKinds.length > 0
    ? opts.includeTrackKinds
    : ["audio", "music", "voice", "sfx"])
    .map((s) => String(s).toLowerCase().trim())
    .filter(Boolean);

  const rawTracks = Array.isArray(state?.tracks) ? state.tracks : [];

  const tracks: PodcastTrackV1[] = rawTracks
    .filter((t) => {
      const k = normStr(t?.kind).toLowerCase();
      if (!k) return includeKinds.includes("audio");
      return includeKinds.includes(k);
    })
    .map((t) => ({
      id: normStr(t.id) || makeId("t"),
      name: typeof t.name === "string" ? t.name : undefined,
      kind: trackKindToPodcastKind(normStr(t.kind) || "audio"),
      muted: !!t.muted,
      gainDb: finiteNum(t.gainDb) ? Number(t.gainDb) : undefined,
    }));

  if (tracks.length === 0) {
    tracks.push({ id: "a1", name: "Audio", kind: "audio" });
    warnings.push("No included audio tracks found; created fallback track a1.");
  }

  const durationClampMax = durationSecIn > 0 ? durationSecIn : Number.POSITIVE_INFINITY;

  const rawClips = Array.isArray(state?.clips) ? state.clips : [];
  const clips: PodcastClipV1[] = [];

  for (const c of rawClips) {
    const id = normStr(c?.id);
    const trackIdRaw = normStr(c?.trackId);

    const startSecRaw = safeTimeSec(c?.startSec, 0);
    const endSecRaw = clipEndSec(c);

    if (!id) {
      warnings.push("A clip is missing id; skipped.");
      continue;
    }
    if (!trackIdRaw) {
      warnings.push(`Clip ${id} missing trackId; skipped.`);
      continue;
    }

    const trackOk = tracks.some((t) => t.id === trackIdRaw);
    const finalTrackId = trackOk ? trackIdRaw : tracks[0].id;
    if (!trackOk) warnings.push(`Clip ${id} referenced unknown trackId=${trackIdRaw}; remapped to ${finalTrackId}.`);

    const s = clamp(startSecRaw, 0, durationClampMax);
    const e = clamp(endSecRaw, s, durationClampMax);

    if (e - s < 0.0005) {
      warnings.push(`Clip ${id} has near-zero duration; skipped.`);
      continue;
    }

    const fadesIn = c?.fades ?? {};
    const fades = {
      inSec: finiteNum(fadesIn?.inSec) ? Math.max(0, Number(fadesIn.inSec)) : undefined,
      outSec: finiteNum(fadesIn?.outSec) ? Math.max(0, Number(fadesIn.outSec)) : undefined,
      curve:
        fadesIn?.curve === "equalPower" || fadesIn?.curve === "linear"
          ? (fadesIn.curve as "linear" | "equalPower")
          : undefined,
    };

    clips.push({
      id,
      trackId: finalTrackId,
      startSec: s,
      endSec: e,
      mediaId: typeof c?.mediaId === "string" && c.mediaId.trim() ? c.mediaId.trim() : undefined,
      url: typeof c?.url === "string" && c.url.trim() ? c.url.trim() : undefined,
      inSec: finiteNum(c?.inSec) ? Math.max(0, Number(c.inSec)) : undefined,
      outSec: finiteNum(c?.outSec) ? Math.max(0, Number(c.outSec)) : undefined,
      gainDb: finiteNum(c?.gainDb) ? Number(c.gainDb) : undefined,
      fades: fades.inSec != null || fades.outSec != null || fades.curve != null ? fades : undefined,
      meta: c?.meta && typeof c.meta === "object" ? c.meta : undefined,
    });
  }

  const rawMarkers = Array.isArray(state?.markers) ? state.markers : [];
  const markers: PodcastMarkerV1[] = rawMarkers
    .map((m) => ({
      id: normStr(m?.id) || makeId("m"),
      timeSec: safeTimeSec(m?.timeSec, 0),
      label: typeof m?.label === "string" ? m.label : undefined,
      kind: typeof m?.kind === "string" ? m.kind : undefined,
    }))
    .filter((m) => m.timeSec >= 0 && (durationSecIn <= 0 ? true : m.timeSec <= durationSecIn + 1e-6));

  const target: PodcastRenderTargetV1 = {
    sampleRate: finiteNum(opts?.target?.sampleRate) ? Number(opts.target!.sampleRate) : 48000,
    channels: (opts?.target?.channels === 1 || opts?.target?.channels === 2 ? opts.target.channels : 2) as 1 | 2,
    loudnessTargetLUFS: finiteNum(opts?.target?.loudnessTargetLUFS) ? Number(opts.target!.loudnessTargetLUFS) : -16,
    truePeakDbTP: finiteNum(opts?.target?.truePeakDbTP) ? Number(opts.target!.truePeakDbTP) : -1,
  };

  const durationSecOut = durationSecIn > 0 ? durationSecIn : inferDurationFromClips(clips, warnings);

  const out: PodcastExportV1 = {
    v: 1,
    episode: opts.episode ?? {},
    provenance: {
      sessionId: opts.sessionId ?? null,
      projectId: opts.projectId ?? null,
      buildHash: opts.buildHash ?? null,
      exportedAtEpochMs: Date.now(),
      source: opts.source ?? "timeline",
      app: "synktribe-core-ui",
      contractVersion: 1,
    },
    target,
    durationSec: Math.max(0, durationSecOut),
    tracks,
    clips: sortByTime(clips),
    markers: sortByMarkerTime(markers),
    warnings,
    report: {
      build: {
        sampleRate: target.sampleRate,
        channels: target.channels,
        builtAtEpochMs: Date.now(),
        buildTimeMs: Math.max(0, nowMs() - t0),
      },
    },
  };

  if (out.clips.length === 0) warnings.push("No audio clips included (export will be silent).");

  return out;
}

/* =========================================================
 * 2) Offline render → WAV Blob
 * ======================================================= */

async function resampleAndAdaptToTarget(
  input: AudioBuffer,
  targetSampleRate: number,
  targetChannels: 1 | 2,
  OfflineCtor: typeof OfflineAudioContext,
): Promise<AudioBuffer> {
  const inSR = input.sampleRate;
  const inCh = input.numberOfChannels;

  const needResample = Math.round(inSR) !== Math.round(targetSampleRate);
  const needChannelAdapt = inCh !== targetChannels;

  if (!needResample && !needChannelAdapt) return input;

  const frames = Math.max(1, Math.ceil(input.duration * targetSampleRate));
  const ctx = new OfflineCtor(targetChannels, frames, targetSampleRate);

  const src = ctx.createBufferSource();
  src.buffer = input;

  let node: AudioNode = src;

  if (needChannelAdapt) {
    if (targetChannels === 2) {
      if (inCh === 1) {
        const splitter = ctx.createChannelSplitter(1);
        const merger = ctx.createChannelMerger(2);
        node.connect(splitter);
        splitter.connect(merger, 0, 0);
        splitter.connect(merger, 0, 1);
        node = merger;
      } else {
        const splitter = ctx.createChannelSplitter(Math.max(2, inCh));
        const merger = ctx.createChannelMerger(2);
        node.connect(splitter);
        splitter.connect(merger, 0, 0);
        splitter.connect(merger, 1, 1);
        node = merger;
      }
    } else {
      const splitter = ctx.createChannelSplitter(Math.max(2, inCh));
      const merger = ctx.createChannelMerger(1);
      const gL = ctx.createGain();
      const gR = ctx.createGain();
      gL.gain.value = 0.5;
      gR.gain.value = 0.5;

      node.connect(splitter);
      splitter.connect(gL, 0);
      splitter.connect(gR, Math.min(1, inCh - 1));
      gL.connect(merger, 0, 0);
      gR.connect(merger, 0, 0);

      node = merger;
    }
  }

  node.connect(ctx.destination);
  src.start(0);

  return await ctx.startRendering();
}

export async function renderPodcastExportToWavBlob(exp: PodcastExportV1, opts: RenderPodcastWavOptions): Promise<Blob> {
  const OfflineCtor = opts.OfflineAudioContextCtor ?? (globalThis as any).OfflineAudioContext;
  if (!OfflineCtor) throw new Error("OfflineAudioContext is not available in this environment.");

  const sr = exp.target.sampleRate;
  const ch = exp.target.channels;

  const durationSec = Math.max(0, exp.durationSec);
  const lengthFrames = Math.ceil(durationSec * sr);
  const ctx = new OfflineCtor(ch, Math.max(1, lengthFrames), sr);

  // Master bus (lets us clamp & post-process deterministically)
  const master = ctx.createGain();
  master.gain.value = 1;

  // Best-effort compressor/limiter (keeps mixes safer before final attenuation)
  const comp = ctx.createDynamicsCompressor();
  comp.threshold.setValueAtTime(-10, 0);
  comp.knee.setValueAtTime(12, 0);
  comp.ratio.setValueAtTime(12, 0);
  comp.attack.setValueAtTime(0.003, 0);
  comp.release.setValueAtTime(0.25, 0);

  master.connect(comp);
  comp.connect(ctx.destination);

  const total = exp.clips.length;
  opts.onProgress?.({ stage: "prepare", done: 0, total });

  // Per-track gain (muted => 0) routed to master
  const trackGain = new Map<string, GainNode>();
  for (const t of exp.tracks) {
    const g = ctx.createGain();
    const db = finiteNum(t.gainDb) ? Number(t.gainDb) : 0;
    g.gain.value = t.muted ? 0 : dbToGain(db);
    g.connect(master);
    trackGain.set(t.id, g);
  }

  let done = 0;

  for (const clip of exp.clips) {
    const track = exp.tracks.find((t) => t.id === clip.trackId) ?? exp.tracks[0];
    if (track?.muted) {
      done += 1;
      opts.onProgress?.({ stage: "placeClips", done, total });
      continue;
    }

    const gTrack = trackGain.get(track.id) ?? master;

    // Decode/resample cache
    const cacheKey = makeAudioCacheKey({
      clipId: clip.id,
      mediaId: clip.mediaId,
      url: clip.url,
      sr,
      ch,
      inSec: safeTimeSec(clip.inSec, 0),
      outSec: finiteNum(clip.outSec) ? Number(clip.outSec) : null,
    });

    const buf = await (__audioCache.get(cacheKey) ??
      (() => {
        const p = (async () => {
          const rawBuf = await opts.resolveAudio({ clip, track, export: exp });
          return await resampleAndAdaptToTarget(rawBuf, sr, ch, OfflineCtor);
        })();
        __audioCache.set(cacheKey, p);
        return p;
      })());

    // Timeline window
    const clipStart = Math.max(0, clip.startSec);
    const clipEnd = Math.max(clipStart, clip.endSec);
    const clipDur = Math.max(0.0005, clipEnd - clipStart);

    // Clamp to export duration (avoid placing past end)
    const maxDur = Math.max(0, durationSec - clipStart);
    const clipDurClamped = Math.max(0.0005, Math.min(clipDur, maxDur));
    if (clipDurClamped <= 0.0005) {
      done += 1;
      opts.onProgress?.({ stage: "placeClips", done, total });
      continue;
    }

    // Source trim window
    const inSec = safeTimeSec(clip.inSec, 0);
    const outSecRaw = finiteNum(clip.outSec) ? Math.max(inSec, Number(clip.outSec)) : null;

    const bufDur = Math.max(0, buf?.duration ?? 0);
    const srcAvail = Math.max(0, bufDur - inSec);
    if (srcAvail <= 0.0001) {
      done += 1;
      opts.onProgress?.({ stage: "placeClips", done, total });
      continue;
    }

    const trimmedAvail = outSecRaw != null ? Math.max(0, Math.min(srcAvail, outSecRaw - inSec)) : srcAvail;

    const playDur = Math.max(0.0005, Math.min(clipDurClamped, trimmedAvail));
    if (playDur <= 0.0005) {
      done += 1;
      opts.onProgress?.({ stage: "placeClips", done, total });
      continue;
    }

    // Optional: resolved media for registry hints (loop/gain/fades/LUFS trim)
    let resolved: ResolvedMediaV1 | null = null;
    try {
      resolved = opts.resolveMedia ? await opts.resolveMedia({ clip, track, export: exp }) : null;
    } catch {
      resolved = null;
    }

    // Hints: registry → render controls (fallback to clip fields)
    const fallbackCurve: "linear" | "equalPower" = clip.fades?.curve === "equalPower" ? "equalPower" : "linear";

    const hints = resolved
      ? getPodcastRenderHintsFromResolved(resolved, {
          targetLUFS: exp.target.loudnessTargetLUFS ?? -16,
        })
      : {
          loop: false,
          gain: dbToGain(finiteNum(clip.gainDb) ? Number(clip.gainDb) : 0),
          fadeInSec: safeTimeSec(clip.fades?.inSec, 0),
          fadeOutSec: safeTimeSec(clip.fades?.outSec, 0),
          curve: fallbackCurve,
        };

    const baseGain = Math.max(0, Number((hints as any).gain ?? 1));
    const fadeInReq = Math.max(0, Number((hints as any).fadeInSec ?? 0));
    const fadeOutReq = Math.max(0, Number((hints as any).fadeOutSec ?? 0));
    const curve: "linear" | "equalPower" = (hints as any).curve === "equalPower" ? "equalPower" : fallbackCurve;

    const fadeInEff = Math.min(fadeInReq, playDur);
    const fadeOutEff = Math.min(fadeOutReq, playDur);

    // Create and schedule source
    const src = ctx.createBufferSource();
    src.buffer = buf;

    // Loop wiring: loop within the trimmed region if possible
    const wantLoop = !!(hints as any).loop;
    if (wantLoop) {
      const loopStart = inSec;
      const loopEnd =
        outSecRaw != null ? Math.min(outSecRaw, bufDur) : Math.min(bufDur, Math.max(loopStart + 0.01, bufDur));

      if (Number.isFinite(loopStart) && Number.isFinite(loopEnd) && loopEnd - loopStart >= 0.01) {
        src.loop = true;
        src.loopStart = loopStart;
        src.loopEnd = loopEnd;
      } else {
        src.loop = false;
      }
    } else {
      src.loop = false;
    }

    // Deterministic: enforce play window and stop for looped sources
    src.start(clipStart, inSec, playDur);
    try {
      src.stop(clipStart + playDur);
    } catch {
      // ignore
    }

    // Gain + fades node (from registry hints when available)
    const gainNode = wireClipGainAndFades({
      ctx,
      source: src,
      clipStartSec: clipStart,
      clipDurSec: playDur,
      baseGain,
      fadeInSec: fadeInEff,
      fadeOutSec: fadeOutEff,
      curve,
    });

    gainNode.connect(gTrack);

    done += 1;
    opts.onProgress?.({ stage: "placeClips", done, total });
  }

  // Render
  opts.onProgress?.({ stage: "render", done: 0, total: 1 });
  const rt0 = nowMs();
  const rendered = await ctx.startRendering();
  const rt1 = nowMs();
  const renderTimeMs = Math.max(0, rt1 - rt0);

  /* ---------------------------------------------------------
   * Deterministic peak measurement + attenuation to target
   * ------------------------------------------------------- */

  const targetDbTP = finiteNum(exp.target.truePeakDbTP) ? Number(exp.target.truePeakDbTP) : -1;
  const targetLin = dbToGain(targetDbTP);

  const nativePeak = maxAbsSamplePeak(rendered);
  const runBetween = nativePeak >= targetLin * 0.92;
  const peakLinBefore = runBetween ? Math.max(nativePeak, truePeakish(rendered, { deltaGate: 0.12, steps: 4 })) : nativePeak;

  const measuredPeakDbBefore = peakDbFromLin(peakLinBefore);

  // Attenuate only (never amplify)
  let appliedMasterGain = 1.0;
  if (Number.isFinite(targetLin) && targetLin > 0 && peakLinBefore > targetLin + 1e-7) {
    appliedMasterGain = Math.min(1.0, Math.max(0.0, targetLin / peakLinBefore));
    applyGainInPlace(rendered, appliedMasterGain);
  }

  const peakLinAfter = maxAbsSamplePeak(rendered);
  const measuredPeakDb = peakDbFromLin(peakLinAfter);

  // Attach v1-safe report render metrics
  exp.report = exp.report ?? {
    build: {
      sampleRate: exp.target.sampleRate,
      channels: exp.target.channels,
      builtAtEpochMs: Date.now(),
    },
  };

  exp.report.render = {
    sampleRate: exp.target.sampleRate,
    channels: exp.target.channels,
    renderTimeMs,
    measuredPeakDb,
    measuredPeakDbBefore,
    appliedMasterGain,
    appliedMasterGainDb: gainToDb(appliedMasterGain),
  };

  // Encode WAV (PCM16)
  const wav = encodeWavFromAudioBuffer(rendered, { encoding: "pcm16" });

  opts.onProgress?.({ stage: "done", done: 1, total: 1 });
  return wav.blob;
}

/* =========================================================
 * Convenience helpers
 * ======================================================= */

export function exportPodcastJsonBlob(exp: PodcastExportV1): Blob {
  return new Blob([JSON.stringify(exp, null, 2)], { type: "application/json" });
}

export function exportPodcastJsonFilename(exp: PodcastExportV1): string {
  const t = exp.episode?.title?.trim() || "podcast-episode";
  const safe = t.replace(/[^\w\-]+/g, "_").slice(0, 64);
  return `${safe}.podcastExport.v1.json`;
}


